import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import {
  useWsmFeeDeleteMutation,
  useWsmFeeDetailsQuery,
  useWsmFeeUpdateMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { Skeleton } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import WsmFeeDetailsPage from "../components/WsmFeeDetailsPage/WsmFeeDetailsPage";
import { sectionMessages } from "../messages";
import { wsmFeeListPath } from "../urls";
import { feeToFormData, getFeeUpdateInput } from "../utils";

export const WsmFeeDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { data, loading } = useWsmFeeDetailsQuery({ variables: { id } });
  const [updateFee, updateFeeOpts] = useWsmFeeUpdateMutation({
    onCompleted: result => {
      if (!result.wsmFeeUpdate?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
      }
    },
  });
  const [deleteFee, deleteFeeOpts] = useWsmFeeDeleteMutation({
    onCompleted: result => {
      if (!result.wsmFeeDelete?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmFeeListPath);
      } else {
        setDeleteOpen(false);
      }
    },
  });
  const fee = data?.wsmFee;
  const errors = updateFeeOpts.data?.wsmFeeUpdate?.errors ?? [];

  if (loading || !fee) {
    return (
      <DetailPageLayout>
        <TopNav href={wsmFeeListPath} title={intl.formatMessage(sectionMessages.feeListTitle)} />
        <DetailPageLayout.Content>
          <Skeleton />
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  return (
    <>
      <WsmFeeDetailsPage
        key={fee.id}
        title={fee.label}
        productName={fee.product.name}
        currencyCode={fee.currencyCode}
        initialData={feeToFormData(fee)}
        errors={errors}
        disabled={updateFeeOpts.loading}
        saveButtonBarState={getMutationState(updateFeeOpts.called, updateFeeOpts.loading, errors)}
        onSubmit={formData =>
          updateFee({ variables: { id: fee.id, input: getFeeUpdateInput(formData) } })
        }
        onDelete={() => setDeleteOpen(true)}
      />
      <ActionDialog
        open={deleteOpen}
        variant="delete"
        title={intl.formatMessage(sectionMessages.feeDeleteTitle)}
        confirmButtonState={getMutationState(deleteFeeOpts.called, deleteFeeOpts.loading, [])}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteFee({ variables: { id: fee.id } })}
      >
        {intl.formatMessage(sectionMessages.feeDeleteContent, { name: fee.label })}
      </ActionDialog>
    </>
  );
};

export default WsmFeeDetailsView;
